<script lang="ts">
    // A Bootstrap dropdown.
    import { onDestroy, onMount, tick } from 'svelte'

    import Icon from '@iconify/svelte'
    import { isEqual } from 'lodash-es'

    import { gettext as _, resolve } from 'hydria/services/gettext'

    import { lastDropdownIdNumber, openDropdownId } from './dropdown'

    interface Props {
        placeholder?: string | null
        placeholderHtml?: string | null
        placeholderComponent?: any
        forcePlaceholder?: boolean
        // Selected container
        selected?: any | null
        // What to use for "nothing selected"
        empty?: string | null
        // "id", null means the whole object
        id?: any
        // Compare items to select which is selected
        compare?: (a: any, b: any) => boolean
        // property containing the text to show in the menu
        text?: any
        // property containing the text to show when closed; null = fallback to `text`
        selectedText?: any
        items: any[]
        itemFiltering?: undefined | ((items: any[], text: string) => any[])
        clear?: string | null
        disabled?: boolean
        alignRight?: boolean
        alignTop?: boolean
        textIsHtml?: boolean | 'component'
        size?: null | 'sm' | 'xs'
        ddHeight?: null | 'sm'
        btnClasses?: string
        inline?: boolean
        block?: boolean
        selectable?: null | ((item: any, index?: number) => boolean)
        itemClass?: null | ((item: any, index?: number) => string)

        onButtonElement?: (e: HTMLButtonElement) => void
        onSelect?: (i: any) => void
    }

    let { placeholder = null, placeholderHtml = null, placeholderComponent = null, forcePlaceholder = false, selected = null, empty = null, id = null, compare = undefined, text = null, selectedText = null, items, itemFiltering = undefined, clear = null, disabled = false, alignRight = false, alignTop = false, textIsHtml = false, size = null, ddHeight = null, btnClasses = 'btn-outline-secondary', inline = false, block = false, selectable = null, itemClass = null, onButtonElement = (e: HTMLButtonElement) => {}, onSelect = (i: any) => {} }: Props = $props()

    let buttonElement: HTMLButtonElement | undefined = $state()

    // Temps entre reset typing quand on tappe une nouvelle lettre
    const AUTO_KEY_RESET_DELAY_MS = 1500

    // let open = false

    let activePos: number | null = $state(null)

    let filteredItems: any[] = $state([...items])
    let filterText: string = $state('')

    function doFiltering(items: any[], filterText: string) {
        let activeItem = activePos != null ? filteredItems[activePos] : null
        if (itemFiltering != null) {
            if (filterText != '') {
                filteredItems = itemFiltering(items, filterText)
            } else {
                filteredItems = [...items]
            }
        } else {
            filterText = ''
            filteredItems = [...items]
        }
        if (activeItem) {
            const pos = filteredItems.indexOf(activeItem)
            if (pos < 0) {
                activePos = null
            } else {
                activePos = pos
            }
        }
        // Auto-select first match
        if (activePos == null && filteredItems.length) activePos = 0
    }

    let selectedObject: any = $derived.by(() => {
        let found: any = undefined
        if (selected != null && filteredItems) {
            const selv = $state.snapshot(selected)
            for (const [key, o] of filteredItems.entries()) {
                const v = $state.snapshot(o)
                if ((compare != null && compare(id != null ? v[id] : v, selv)) || (id != null && isEqual(v[id], selv)) || isEqual(v, selv)) {
                    found = o
                    break
                }
            }
        }
        if (found === undefined) {
            return null
        }
        return found
    })

    onMount(() => {
        doFiltering(items, filterText)
    })

    const myId: string = `sv-dropdown-${$lastDropdownIdNumber + 1}`
    $lastDropdownIdNumber += 1

    $effect(() => {
        if (disabled && $openDropdownId == myId) $openDropdownId = null
    })

    $effect(() => {
        if (buttonElement && onButtonElement) onButtonElement(buttonElement)
    })

    function setActivePosition(): number | null {
        if (selected != null && filteredItems) {
            for (const [key, o] of filteredItems.entries()) {
                if ((id != null && o[id] === selected) || o === selected) {
                    activePos = key
                    return key
                }
            }
        }
        activePos = null
        return null
    }

    function toggleOpen() {
        if ($openDropdownId === myId) {
            return close()
        }
        open()
    }
    function close() {
        if ($openDropdownId === myId) {
            $openDropdownId = null
            filterText = ''
            activePos = null
            doFiltering(items, filterText)
        }
    }
    function open() {
        if ($openDropdownId !== myId) {
            setActivePosition()
            $openDropdownId = myId
            tick().then(() => {
                ensureActiveInView()
            })
        }
    }

    function clearSelection() {
        if (disabled) return
        selectedObject = null
        // selectedPos = null
        onSelect(empty)
        close()
    }

    function select(what: any) {
        if (disabled) return
        if (what == null || what === '') {
            selectedObject = null
            onSelect(empty)
        } else {
            if (selected != null) {
                selectedObject = what
            }

            if (id == null) {
                onSelect(what)
            } else {
                onSelect(what[id])
            }
        }
        close()
    }

    function whateverToText(what: any, ...ops: any[]): string {
        for (const op of ops) {
            if (op == null) continue
            if (typeof op == 'function') {
                return op($state.snapshot(what))
            }
            return $resolve(what[op])
        }
        return $resolve(what)
    }

    // Scrolling element containing items
    let scrollElement: HTMLDivElement | undefined = $state()

    function itemInView(item: object, ensureInView?: boolean): boolean
    function itemInView(pos: number, ensureInView?: boolean): boolean
    function itemInView(arg: any, ensureInView: boolean = false): boolean {
        let itemPos: number
        if (typeof arg == 'number') {
            itemPos = arg
        } else {
            itemPos = filteredItems.indexOf(arg)
        }
        if (itemPos < 0) return false

        const itemId = `${myId}-litm-${itemPos}`
        const itemEl = document.getElementById(itemId)
        if (!itemEl) return false

        if (!scrollElement) return false

        let ob = scrollElement.getBoundingClientRect()
        let ib = itemEl.getBoundingClientRect()

        const inView = Boolean(ib.top >= ob.top && ib.bottom <= ob.bottom)
        if (ensureInView && !inView) {
            itemEl.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' })
        }
        return inView
    }

    function ensureActiveInView() {
        if (activePos == null) return
        itemInView(activePos, true)
    }

    // Key used to open or whatever the listbox
    const actionKeys = ['ArrowDown', 'ArrowUp', 'Enter', ' ']
    const clearKeys = ['Clear', 'Delete', 'Backspace']

    let lastKeyDown: number | undefined = undefined
    let lastKeyTimer: ReturnType<typeof setTimeout> | undefined = undefined
    let lastKeyInTimer: boolean = $state(false)

    onDestroy(() => {
        clearKeyDownTimer()
    })
    function clearKeyDownTimer() {
        if (lastKeyTimer != null) {
            clearTimeout(lastKeyTimer)
            lastKeyTimer = undefined
        }
        lastKeyInTimer = false
    }

    function markKeyDown() {
        if (lastKeyTimer != null) clearTimeout(lastKeyTimer)
        lastKeyTimer = setTimeout(() => {
            lastKeyInTimer = false
        }, AUTO_KEY_RESET_DELAY_MS)
        lastKeyInTimer = true
    }

    function handleKeydown(e: KeyboardEvent) {
        if ($openDropdownId != myId) {
            if (actionKeys.includes(e.key)) {
                // $openDropdownId = myId
                open()
                e.preventDefault()
            }
            return
        }

        if (['Backspace', 'Escape'].includes(e.key) && filterText != '') {
            filterText = ''
            doFiltering(items, filterText)
            return
        }

        if (e.key == 'Escape') {
            // $openDropdownId = null
            close()
            e.preventDefault()
            return
        }
        if (e.key == 'ArrowUp') {
            if (activePos == null) activePos = filteredItems.length - 1
            else if (activePos > 0) activePos -= 1
            e.preventDefault()
            ensureActiveInView()
            return
        } else if (e.key == 'ArrowDown') {
            if (activePos == null) activePos = 0
            else if (activePos < filteredItems.length - 1) activePos += 1
            e.preventDefault()
            ensureActiveInView()
            return
        }

        if ([' ', 'Enter'].includes(e.key) && activePos != null) {
            select(filteredItems[activePos])
            e.preventDefault()
            return
        }

        if (clearKeys.includes(e.key)) {
            clearSelection()
            e.preventDefault()
            return
        }

        if (itemFiltering == null) return
        if (e.key.toLocaleLowerCase().match(/^[a-z0-9_-]$/)) {
            const now = new Date().valueOf()
            if (lastKeyDown == null || now - lastKeyDown > AUTO_KEY_RESET_DELAY_MS) {
                filterText = e.key.toLocaleLowerCase()
            } else {
                filterText += e.key.toLocaleLowerCase()
            }
            markKeyDown()
            lastKeyDown = now
            doFiltering(items, filterText)
            return
        }
    }
</script>

<style>
    :root {
        /* **MUST** be smaller than --bs-dropdown-item-padding-y and -x */
        --wdom-dropdown-active-border: 0.2em;
    }
    .dropdown-item {
        cursor: pointer;
    }
    .dropdown-item.non-selectable {
        cursor: default;
        opacity: 0.5;
    }
    .dropdown-item.non-selectable:hover {
        color: inherit;
        background-color: inherit;
    }

    .dropdown-item:global(.indented) {
        padding-left: 2rem;
    }
    .dropdown-item.key-active {
        border: var(--wdom-dropdown-active-border) solid var(--bs-dropdown-link-active-bg);
    }
    .dropdown-item {
        padding: calc(var(--bs-dropdown-item-padding-y) - var(--wdom-dropdown-active-border)) calc(var(--bs-dropdown-item-padding-x) - var(--wdom-dropdown-active-border));
        border: var(--wdom-dropdown-active-border) solid transparent;
    }
    .dropdown-filter-text {
        position: absolute;
        z-index: 10;
        top: 0.25em;
        bottom: auto;
        right: 2.5em;
        display: flex;
        align-items: center;
        justify-content: center;
        background: white;
        font-size: 0.8em;
        /* box-shadow: 0 0 2px white; */
        border: 1px solid var(--bs-border-color-translucent);
        border-radius: 3px;
        padding: 0 0.5em;
    }
    /* Focus outline only shows up on <input> unless we actually focus with the keyboard.
       So when we focus in JS, there’s no visual cue.
       So we 'copy' BS's :focus-visible on :focus on this instance.
       */
    button.dropdown-toggle:focus {
        background-color: var(--bs-btn-hover-bg);
        border-color: var(--bs-btn-hover-border-color);
        box-shadow: var(--bs-btn-focus-box-shadow);
        color: var(--bs-btn-hover-color);
        outline: 0px;
    }

    button.dropdown-toggle {
        max-width: 100%;
    }
    button.dropdown-toggle > .sel-text {
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
    }
</style>

<svelte:body
    onclick={(e) => {
        if ($openDropdownId == myId) close()
    }}
/>

<div class="dropdown dropdown-selstyle" class:open={$openDropdownId === myId} class:dropdown-block={block} style={inline ? 'display: inline-block' : ''}>
    <button
        class="btn {btnClasses} dropdown-toggle {size ? `btn-${size}` : ''}"
        class:no-sel={selectedObject == null || (empty != null && selectedObject === empty)}
        onclick={(e) => {
            e.stopPropagation()
            toggleOpen()
        }}
        type="button"
        id={myId}
        role="combobox"
        aria-controls={`${myId}-lbox`}
        aria-haspopup="listbox"
        aria-expanded={$openDropdownId == myId ? 'true' : 'false'}
        aria-labelledby={`${myId}-txt`}
        {disabled}
        onkeydown={(e) => handleKeydown(e)}
        aria-activedescendant={$openDropdownId == myId && activePos != null ? `${myId}-litm-${activePos}` : null}
        bind:this={buttonElement}
    >
        <span class="sel-text" id={`${myId}-txt`}>
            {#if forcePlaceholder || selectedObject == null || (empty != null && selectedObject === empty)}
                {#if placeholder}
                    {$resolve(placeholder)}
                {:else if placeholderHtml}
                    {@html $resolve(placeholderHtml)}
                {:else if placeholderComponent}
                    {@const SvelteComponent = placeholderComponent}
                    <SvelteComponent />
                {:else}–
                {/if}
            {:else if textIsHtml === 'component'}
                {@const SvelteComponent_1 = text}
                <SvelteComponent_1 item={selectedObject} isSelected={true} isPlaceholder={true} />
            {:else if textIsHtml}
                {@html whateverToText(selectedObject, selectedText, text)}
            {:else}
                {whateverToText(selectedObject, selectedText, text)}
            {/if}
        </span>
        {#if clear != null && clear == '' && selectedObject != null && !(empty != null && selectedObject === empty)}
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <span
                class="clear-sel"
                onclick={(e) => {
                    e.stopPropagation()
                    clearSelection()
                }}
                tabindex="-1"
                role="button"><Icon inline icon="mdi:close" /></span
            >
        {/if}
        <span class="caret"></span>
    </button>
    {#if filterText}
        <div class="dropdown-filter-text"><span><Icon inline icon="mdi:filter" /> {filterText}{lastKeyInTimer ? '…' : ''}</span></div>
    {/if}
    <div bind:this={scrollElement} class="dropdown-menu" class:dropdown-menu-small={ddHeight == 'sm'} class:dropdown-menu-right={alignRight} class:dropdown-menu-left={!alignRight} class:dropdown-menu-top={alignTop} aria-labelledby={myId} role="listbox" class:show={$openDropdownId == myId} id={`${myId}-lbox`}>
        {#if clear != null && clear !== ''}
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <div
                class="dropdown-item"
                class:active={selectedObject == null}
                onclick={(e) => {
                    e.stopPropagation()
                    clearSelection()
                }}
                tabindex="-1"
                role="button"
            >
                <span>{$resolve(clear)}</span>
            </div>

            <div role="separator" class="dropdown-divider"></div>
        {/if}
        {#if filteredItems && filteredItems.length}
            {#each filteredItems as item, i}
                {#if selectable == null || selectable(item, i)}
                    <!-- svelte-ignore a11y_click_events_have_key_events -->
                    <div
                        class="dropdown-item {itemClass != null ? itemClass(item, i) : ''}"
                        class:active={selectedObject === item}
                        onclick={(e) => {
                            e.stopPropagation()
                            select(item)
                        }}
                        id={`${myId}-litm-${i}`}
                        class:key-active={i === activePos}
                        role="option"
                        aria-selected={selectedObject === item}
                        tabindex="-1"
                    >
                        <span
                            >{#if textIsHtml === 'component'}{@const SvelteComponent_2 = text}
                                <SvelteComponent_2 {item} isSelected={selectedObject === item} />{:else if !textIsHtml}{whateverToText(item, text)}{:else}{@html whateverToText(item, text)}{/if}</span
                        >
                    </div>
                {:else}
                    <div class="dropdown-item non-selectable {itemClass != null ? itemClass(item, i) : ''}">
                        <span>
                            {#if textIsHtml === 'component'}{@const SvelteComponent_3 = text}
                                <SvelteComponent_3 {item} isSelected={selectedObject === item} nonSelectable={true} />{:else if !textIsHtml}{whateverToText(item, text)}{:else}{@html whateverToText(item, text)}{/if}
                        </span>
                    </div>
                {/if}
            {/each}
        {:else}
            <div class="dropdown-item non-selectable dropdown-filter-empty">
                <span>{$_('(aucun résultat)')}</span>
            </div>
        {/if}
    </div>
</div>
